import { useSearchParams } from "react-router-dom";
import React, { useEffect, useState } from "react";

import { SubscribedUsersPanel } from "./components/SubscribedUsersPanel";
import { ErrorEventsPanel } from "./components/ErrorEventsPanel";
import { AnalyticsPanel } from "./components/AnalyticsPanel";
import { ManagersPanel } from "./components/ManagersPanel";
import { UsersPanel } from "./components/UsersPanel";

type PanelTab =
  | "users"
  | "managers"
  | "subscribers"
  | "error-events"
  | "analytics";

const PANEL_TABS: Array<{ id: PanelTab; title: string; description: string }> =
  [
    {
      id: "users",
      title: "Пользователи",
      description: "Аккаунты, доступ к маркету и объявления",
    },
    {
      id: "managers",
      title: "Менеджеры",
      description: "Назначение роли и управление действующими менеджерами",
    },
    {
      id: "subscribers",
      title: "Подписчики",
      description: "Тарифы и сроки действия подписок",
    },
    {
      id: "analytics",
      title: "Аналитика",
      description: "Продуктовые события из выгрузки Яндекс.Метрики",
    },
    {
      id: "error-events",
      title: "Журнал ошибок",
      description: "Ошибки Маркета и безопасная техническая диагностика",
    },
  ];

export const ControlPanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tabFromUrl: PanelTab =
    tabParam === "managers" ||
    tabParam === "subscribers" ||
    tabParam === "error-events" ||
    tabParam === "analytics"
      ? tabParam
      : "users";
  const [tab, setTab] = useState<PanelTab>(tabFromUrl);
  const activeTab = PANEL_TABS.find((item) => item.id === tab) || PANEL_TABS[0];

  useEffect(() => {
    setTab(tabFromUrl);
  }, [tabFromUrl]);

  const selectTab = (nextTab: PanelTab) => {
    setTab(nextTab);
    if (nextTab === "users") setSearchParams({});
    else setSearchParams({ tab: nextTab });
  };

  useEffect(() => {
    const savedPosition = Number(
      sessionStorage.getItem("control-panel:return-scroll-y"),
    );
    if (!Number.isFinite(savedPosition) || savedPosition <= 0) return;

    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: savedPosition });
      sessionStorage.removeItem("control-panel:return-scroll-y");
    });

    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <main className="min-h-[calc(100vh-80px)] bg-[#fafafa] px-0 py-6 sm:px-5 sm:py-8">
      <div className="mx-auto w-full max-w-[1440px]">
        <header className="border-y border-slate-200 bg-white px-4 py-5 sm:rounded-2xl sm:border sm:px-6">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-[#a34a7a]">
                Администрирование
              </p>
              <h1 className="m-0 mt-1 text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-[28px]">
                Контроль-панель
              </h1>
            </div>
            <p className="m-0 max-w-md text-sm leading-5 text-slate-500">
              Управление аккаунтами, доступом к маркету и подписками.
            </p>
          </div>

          <nav
            className="-mb-5 mt-5 flex gap-5 overflow-x-auto"
            aria-label="Разделы контроль-панели"
          >
            {PANEL_TABS.map((item) => {
              const active = item.id === tab;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => selectTab(item.id)}
                  className={[
                    "relative shrink-0 border-b-2 pb-4 text-sm font-semibold transition-colors",
                    active
                      ? "border-[#c06198] text-slate-950"
                      : "border-transparent text-slate-500 hover:text-slate-900",
                  ].join(" ")}
                  aria-current={active ? "page" : undefined}
                >
                  {item.title}
                </button>
              );
            })}
          </nav>
        </header>

        <section className="pt-7 sm:px-1">
          <div className="px-4 sm:px-0">
            <h2 className="m-0 text-lg font-semibold text-slate-950">
              {activeTab.title}
            </h2>
            <p className="m-0 mt-1 text-sm text-slate-500">
              {activeTab.description}
            </p>
          </div>
          <div className="mt-5">
            {tab === "subscribers" ? <SubscribedUsersPanel /> : null}
            {tab === "managers" ? <ManagersPanel /> : null}
            {tab === "analytics" ? <AnalyticsPanel /> : null}
            {tab === "error-events" ? <ErrorEventsPanel /> : null}
            {tab === "users" ? <UsersPanel /> : null}
          </div>
        </section>
      </div>
    </main>
  );
};

export default ControlPanel;
